import { proxyMap } from "valtio/utils";

import { state } from "./State";
import type * as Types from "./types";

export const initialize = (remote: Types.Remote): void => {
  state.remote = remote;
  const liveCoding = remote.entity?.liveCoding;
  const sessions = liveCoding?.sessions;
  sessions?.forEach(session => {
    if (session) {
      state.activeSessionMap.set(session.liveCodingSessionId, session);
    }
  });
  /**
   * Rework references to dispatch Subscribe.
   */
  state.activeSessionMap = proxyMap(state.activeSessionMap);
  const sessionId = sessions?.[0]?.liveCodingSessionId;
  if (sessionId) {
    state.activeSessionId = sessionId;
  }

  state.fetchStatus = "FETCHED";
};

export const clear = () => {
  state.currentEntityIndex = 0;
  state.activeSessionId = null;
  state.waitingActiveSessionId = null;
  state.remote = null;
  state.activeSessionMap = proxyMap();
  state.liveCodingQuestionVariant = "UNKNOWN";
  state.liveCodingTemplateCodesMap = proxyMap();
  state.fetchStatus = "BEFORE_FETCH";
};

const promoteActiveSessionId = () => {
  if (!state.waitingActiveSessionId) {
    return;
  }
  if (state.activeSessionMap.has(state.waitingActiveSessionId)) {
    state.activeSessionId = state.waitingActiveSessionId;
    state.waitingActiveSessionId = null;
  }
};

export const updateRemote = (remote: Types.Remote): void => {
  state.remote = remote;
  const sessions = remote.entity?.liveCoding?.sessions;
  sessions?.forEach(session => {
    if (session) {
      state.activeSessionMap.set(session.liveCodingSessionId, session);
    }
  });
  /**
   * Rework references to dispatch Subscribe.
   */
  state.activeSessionMap = proxyMap(state.activeSessionMap);
  promoteActiveSessionId();
};

export const updateActiveSessionId = (sessionId: Types.LiveCodingSessionId) => {
  if (state.activeSessionMap.has(sessionId)) {
    state.activeSessionId = sessionId;
    state.waitingActiveSessionId = null;
  } else if (sessionId > 0) {
    state.waitingActiveSessionId = sessionId;
  }
};

export const removeSession = (sessionId: Types.LiveCodingSessionId) => {
  state.activeSessionMap.delete(sessionId);
};

export const addSession = (newSession: Types.Session) => {
  state.activeSessionMap.set(newSession.liveCodingSessionId, newSession);
  /**
   * Rework references to dispatch Subscribe.
   */
  state.activeSessionMap = proxyMap(state.activeSessionMap);
};

export const updateQuestionVariant = (challengeCodingEditorVariant: Types.LiveCodingQuestionVariant) => {
  state.liveCodingQuestionVariant = challengeCodingEditorVariant;
};

export const addLiveCodingTemplateCodes = (
  key: Types.LiveCodingTemplateCodesMapKey,
  liveCodingTemplateCodes: Types.LiveCodingTemplateCodes,
) => {
  state.liveCodingTemplateCodesMap.set(key, liveCodingTemplateCodes);
};

export const updateLiveCoding = (liveCoding: Types.LiveCoding) => {
  if (state.remote?.entity) {
    const entity = state.remote.entity;
    entity.liveCoding = liveCoding;
    state.remote.entity = entity;
  }
};

export const setSearchText = (text: string) => {
  state.searchText = text;
};

export const addMetrics = (newMetrics: Types.Metric[]) => {
  newMetrics.forEach(metric => {
    state.metrics.set(metric.metricId, metric);
  });
};

export const updateMetricPagination = (newValue: Partial<Types.MetricPagination>): void => {
  state.metricPagination = {
    ...state.metricPagination,
    ...newValue,
  };
};

export const selectMetric = (id: Types.MetricId) => {
  state.selectedMetricIds.add(id);
};

export const selectMetrics = (ids: Types.MetricId[]) => {
  ids.forEach(id => {
    state.selectedMetricIds.add(id);
  });
};

export const removeMetric = (id: Types.MetricId) => {
  state.selectedMetricIds.delete(id);
};
